import { Avatar, EllipsisIcon, UnstyledButton } from '@buffer-mono/popcorn'

import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseFacebookLinks } from '~publish/legacy/utils/channels/facebook/helpers'

import { FacebookPostActions } from './FacebookPostActions'
import { GlobeIcon } from '../../../../assets/facebook'
import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import { PostWrapper } from '../../../Common/styles'
import {
  FormatLayout,
  getMediaProperties,
  gifAsImage,
} from '../../../Common/utils'
import { VideoPlayer } from '../../../Common/VideoPlayer'
import type { FacebookContent, ProfilePreview } from '../../../types'
import { formatImagesInRows, getImageLayout } from '../../utils'

import styles from './Post.module.css'

import type { JSX } from 'react'

const shortenUsername = (name: string): string => {
  if (name.length > 25) {
    return name.substring(0, 16) + '…' + name.substring(name.length - 5)
  }
  return name
}

const FirstCommentIndicator = (): JSX.Element => (
  <div className={styles.firstCommentIndicator}>1 comment</div>
)

const FirstComment = ({
  avatar,
  username,
  commentText,
}: {
  avatar: string
  username: string
  commentText: string
}): JSX.Element => {
  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({
      channel: 'facebook',
      text: commentText,
      customCharLimit: 120,
    })
  const usernameToDisplay = shortenUsername(username)
  return (
    <div className={styles.singleCommentSection}>
      <Avatar
        data-testid="comment-avatar"
        src={avatar}
        alt={username}
        size="small"
        fallback="image"
      />
      <div className={styles.commentWrapper}>
        <div className={styles.commentHeader}>
          <div className={styles.commentUsernameWrapper}>
            <span translate="no">{usernameToDisplay}</span>{' '}
            <span className={styles.authorTag}>Author</span>
          </div>
        </div>
        <div>
          <span className={styles.linkifiedCommentText}>
            <LinkifiedText
              links={parseFacebookLinks(textToDisplay, [])}
              size="mini"
              whitespace="pre-wrap"
              newTab
            >
              {textToDisplay}
            </LinkifiedText>
          </span>
          {isTruncated && !isExpanded && (
            <UnstyledButton onClick={expand} className={styles.seeMoreButton}>
              {' '}
              ...see more
            </UnstyledButton>
          )}
        </div>
      </div>
    </div>
  )
}

const FacebookPost = ({
  content,
  profile,
}: {
  content: FacebookContent
  profile: ProfilePreview
}): JSX.Element => {
  const { avatar, username } = profile
  const { text, images, gif, video, link: linkFromPost, entities } = content
  const commentText = content?.channelData?.facebook?.comment_text
  const [linkPreview, linkText, scrapedData] = useLinkPreviewEffect({
    linkFromPost,
    text,
    checkValidUrl: false,
    compareWithScraper: true,
    useScraperData: false,
  })
  const { hasImages, hasVideo, hasLink, hasGif } = getMediaProperties(
    content,
    linkPreview,
  )
  const hasScrapedData = !!(scrapedData && scrapedData.thumbnail)
  const hasLinkData = !!(linkPreview && linkPreview.thumbnail)
  const thumbnailEdited =
    hasScrapedData &&
    hasLinkData &&
    scrapedData.thumbnail?.url !== linkPreview.thumbnail?.originalUrl

  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({
      channel: 'facebook',
      text,
      linkToRemove: linkText,
      hasLink,
    })

  const allImages = images?.length ? images : gif ? [gifAsImage(gif)] : []

  return (
    <PostWrapper>
      <div className={styles.header}>
        <Avatar
          data-testid="avatar"
          src={avatar}
          alt={username}
          size="medium"
          fallback="image"
        />
        <div className={styles.usernameWrapper}>
          <span className={styles.username} translate="no">
            {username}
          </span>
          <span className={styles.formattedUsername}>
            {`Just Now · `}
            <GlobeIcon size="small" color="currentColor" />
          </span>
        </div>
        <EllipsisIcon
          size="small"
          color="subtle"
          style={{ marginLeft: 'auto' }}
        />
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <LinkifiedText
            links={parseFacebookLinks(textToDisplay, entities)}
            size="mini"
            whitespace="pre-wrap"
            newTab
          >
            {textToDisplay}
          </LinkifiedText>
          {isTruncated && !isExpanded && (
            <UnstyledButton onClick={expand} className={styles.seeMoreButton}>
              ... See More
            </UnstyledButton>
          )}
        </div>
      </div>
      {(hasImages || hasGif) && (
        <ImagesGrid
          className={styles.imagesGrid}
          images={allImages}
          isRowFormat={getImageLayout(allImages) === FormatLayout.Row}
          formatImages={formatImagesInRows}
        />
      )}
      {hasVideo && video && <VideoPlayer video={video} showSoundOffIcon />}
      {hasLink && linkPreview && (
        <LinkPreview
          link={linkPreview}
          showEmptyThumbnail
          showCompact={thumbnailEdited}
          clickable={false}
        />
      )}
      {commentText && <FirstCommentIndicator />}
      <FacebookPostActions />
      {commentText && (
        <FirstComment
          avatar={avatar}
          username={username}
          commentText={commentText}
        />
      )}
    </PostWrapper>
  )
}

export default FacebookPost
